import jsPDF from 'jspdf';

const translations = {
  en: {
    title: 'RESERVATION CONFIRMATION',
    subtitle: 'Université Senghor — Résidences',
    universitySection: 'UNIVERSITÉ SENGHOR',
    guestSection: '1. GUEST INFORMATION',
    reservationSection: '2. RESERVATION DETAILS',
    additionalSection: '3. ADDITIONAL INFORMATION',
    fullName: 'Full Name',
    nationality: 'Nationality',
    gender: 'Gender',
    passportId: 'Passport / National ID',
    department: 'Department',
    referenceNumber: 'Reference Number',
    building: 'Building',
    roomNumber: 'Room Number',
    checkIn: 'Check-in Date',
    checkOut: 'Check-out Date',
    arrivalTime: 'Expected Arrival Time',
    departureTime: 'Expected Departure Time',
    financialStatus: 'Financial Status',
    adminObservation: 'Admin Observation',
    approvalDate: 'Date of Approval',
    qrCodeLabel: 'Scan QR code to verify reservation',
    covered: 'Covered by Senghor',
    notCovered: 'Not Covered by Senghor',
    male: 'Male',
    female: 'Female',
    other: 'Other',
    footer: 'Université Senghor — Résidences | Official Confirmation Document',
    pageOf: (i, total) => `Page ${i} of ${total}`,
    na: '—',
    officialDoc: 'This document serves as official confirmation of your reservation at Université Senghor Residences.',
  },
  fr: {
    title: 'CONFIRMATION DE RÉSERVATION',
    subtitle: 'Université Senghor — Résidences',
    universitySection: 'UNIVERSITÉ SENGHOR',
    guestSection: '1. INFORMATIONS SUR L\'INVITÉ',
    reservationSection: '2. DÉTAILS DE LA RÉSERVATION',
    additionalSection: '3. INFORMATIONS COMPLÉMENTAIRES',
    fullName: 'Nom complet',
    nationality: 'Nationalité',
    gender: 'Genre',
    passportId: 'Passeport / ID National',
    department: 'Département',
    referenceNumber: 'Numéro de référence',
    building: 'Bâtiment',
    roomNumber: 'Numéro de chambre',
    checkIn: 'Date d\'arrivée',
    checkOut: 'Date de départ',
    arrivalTime: 'Heure d\'arrivée prévue',
    departureTime: 'Heure de départ prévue',
    financialStatus: 'Statut financier',
    adminObservation: 'Observation de l\'administrateur',
    approvalDate: 'Date d\'approbation',
    qrCodeLabel: 'Scanner le QR code pour vérifier la réservation',
    covered: 'Pris en charge par Senghor',
    notCovered: 'Non pris en charge par Senghor',
    male: 'Homme',
    female: 'Femme',
    other: 'Autre',
    footer: 'Université Senghor — Résidences | Document de confirmation officiel',
    pageOf: (i, total) => `Page ${i} sur ${total}`,
    na: '—',
    officialDoc: 'Ce document constitue la confirmation officielle de votre réservation aux Résidences de l\'Université Senghor.',
  },
};

/**
 * Generate a QR code as a data URL using canvas
 */
async function generateQRCodeDataURL(text) {
  try {
    const QRCode = (await import('qrcode'))?.default;
    return await QRCode?.toDataURL(text, {
      width: 120,
      margin: 1,
      color: { dark: '#0f172a', light: '#ffffff' },
    });
  } catch (e) {
    console.warn('QR code generation failed:', e);
    return null;
  }
}

/**
 * Load logo as data URL
 */
async function loadLogoDataURL() {
  try {
    const logoUrl = '/assets/images/logo_USenghor_1000_min-1774524320443.png';
    const response = await fetch(logoUrl);
    const blob = await response?.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Logo load failed:', e);
    return null;
  }
}

/**
 * Get natural image dimensions
 */
async function getImageDimensions(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 1, h: 1 });
    img.src = dataUrl;
  });
}

/**
 * Generate Reservation Confirmation PDF
 * @param {Object} reservation - Reservation data
 * @param {string} language - 'en' or 'fr'
 * @returns {Promise<jsPDF>}
 */
export async function generateReservationConfirmationPDF(reservation, language = 'en') {
  const lx = translations?.[language] || translations?.en;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // ── Load assets ──────────────────────────────────────────────────────────
  const [logoData, qrData] = await Promise.all([
    loadLogoDataURL(),
    generateQRCodeDataURL(reservation?.referenceNumber || reservation?.reference_number || 'USR-UNKNOWN'),
  ]);

  // ── Header ───────────────────────────────────────────────────────────────
  const headerH = 36;
  doc?.setFillColor(15, 23, 42); // dark navy
  doc?.rect(margin, y, contentWidth, headerH, 'F');

  // Logo in header
  let logoEndX = margin + 6;
  if (logoData) {
    try {
      const dims = await getImageDimensions(logoData);
      const logoH = 28;
      const logoW = logoH * (dims?.w / dims?.h);
      doc?.addImage(logoData, 'PNG', margin + 4, y + 4, logoW, logoH);
      logoEndX = margin + 4 + logoW + 6;
    } catch (e) {
      // fallback: no logo
    }
  }

  // Title text in header
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.text(lx?.title, logoEndX, y + 13);
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.text(lx?.subtitle, logoEndX, y + 20);

  // Reference number badge in header
  const refNum = reservation?.referenceNumber || reservation?.reference_number || '—';
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(251, 191, 36); // amber
  doc?.text(refNum, logoEndX, y + 29);

  y += headerH + 6;

  // ── Official notice ───────────────────────────────────────────────────────
  doc?.setFillColor(239, 246, 255);
  doc?.rect(margin, y, contentWidth, 10, 'F');
  doc?.setTextColor(30, 64, 175);
  doc?.setFontSize(8);
  doc?.setFont('helvetica', 'italic');
  doc?.text(lx?.officialDoc, margin + 3, y + 6.5);
  y += 15;

  // ── Section helper ────────────────────────────────────────────────────────
  const drawSection = (title) => {
    doc?.setFillColor(15, 23, 42);
    doc?.rect(margin, y, contentWidth, 8, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text(title, margin + 3, y + 5.5);
    y += 11;
  };

  const drawRow = (label, value) => {
    const labelW = 70;
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(71, 85, 105);
    doc?.text(label + ':', margin + 3, y);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(15, 23, 42);
    const valStr = value != null && value !== '' ? String(value) : lx?.na;
    const lines = doc?.splitTextToSize(valStr, contentWidth - labelW - 6);
    doc?.text(lines, margin + labelW, y);
    y += Math.max(lines?.length * 5, 5) + 2;
  };

  const drawDivider = () => {
    doc?.setDrawColor(226, 232, 240);
    doc?.line(margin, y, pageWidth - margin, y);
    y += 4;
  };

  // ── Guest Information ─────────────────────────────────────────────────────
  drawSection(lx?.guestSection);

  const genderMap = { male: lx?.male, female: lx?.female, other: lx?.other };
  drawRow(lx?.fullName, reservation?.full_name || reservation?.guestName);
  drawRow(lx?.nationality, reservation?.nationality);
  drawRow(lx?.gender, genderMap?.[reservation?.gender] || reservation?.gender);
  drawRow(lx?.passportId, reservation?.national_id || reservation?.passportId);
  drawRow(lx?.department, reservation?.department);
  y += 3;

  // ── Reservation Details ───────────────────────────────────────────────────
  drawSection(lx?.reservationSection);

  // Reference number row with highlight
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(71, 85, 105);
  doc?.text(lx?.referenceNumber + ':', margin + 3, y);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(15, 23, 42);
  doc?.text(refNum, margin + 70, y);
  y += 7;

  drawRow(lx?.building, reservation?.preferred_building || reservation?.building);
  drawRow(lx?.roomNumber, reservation?.room_number || reservation?.room);
  drawRow(lx?.checkIn, reservation?.requested_check_in || reservation?.checkIn);
  drawRow(lx?.checkOut, reservation?.requested_check_out || reservation?.checkOut);
  drawRow(lx?.arrivalTime, reservation?.expected_arrival_time || reservation?.arrivalTime);
  drawRow(lx?.departureTime, reservation?.expected_departure_time || reservation?.departureTime);

  // Financial status with color badge
  const isCovered = (reservation?.financial_status || reservation?.financialStatus) === 'covered_by_senghor';
  const finLabel = isCovered ? lx?.covered : lx?.notCovered;
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(71, 85, 105);
  doc?.text(lx?.financialStatus + ':', margin + 3, y);
  if (isCovered) {
    doc?.setFillColor(220, 252, 231);
    doc?.setTextColor(21, 128, 61);
  } else {
    doc?.setFillColor(255, 237, 213);
    doc?.setTextColor(154, 52, 18);
  }
  doc?.roundedRect(margin + 70, y - 4.5, finLabel?.length * 2.2 + 6, 6.5, 1, 1, 'F');
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(8);
  doc?.text(finLabel, margin + 73, y);
  y += 7;

  if (reservation?.admin_observation || reservation?.adminObservation) {
    drawRow(lx?.adminObservation, reservation?.admin_observation || reservation?.adminObservation);
  }
  y += 3;

  // ── Additional Information ────────────────────────────────────────────────
  drawSection(lx?.additionalSection);

  const approvalDate = reservation?.approved_at
    ? new Date(reservation.approved_at)?.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : new Date()?.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });

  drawRow(lx?.approvalDate, approvalDate);
  y += 4;

  // ── QR Code ───────────────────────────────────────────────────────────────
  if (qrData) {
    const qrSize = 35;
    const qrX = pageWidth - margin - qrSize;
    const qrY = y - 20; // position alongside additional info

    // QR border box
    doc?.setDrawColor(226, 232, 240);
    doc?.setFillColor(255, 255, 255);
    doc?.roundedRect(qrX - 3, qrY - 3, qrSize + 6, qrSize + 12, 2, 2, 'FD');

    try {
      doc?.addImage(qrData, 'PNG', qrX, qrY, qrSize, qrSize);
    } catch (e) {
      // QR failed silently
    }

    doc?.setFontSize(6.5);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(100, 116, 139);
    const qrLabel = doc?.splitTextToSize(lx?.qrCodeLabel, qrSize + 6);
    doc?.text(qrLabel, qrX - 3 + (qrSize + 6) / 2, qrY + qrSize + 4, { align: 'center' });
  }

  y += 10;
  drawDivider();

  // ── Footer ────────────────────────────────────────────────────────────────
  const pageCount = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc?.setPage(i);
    doc?.setDrawColor(226, 232, 240);
    doc?.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    doc?.setFontSize(7.5);
    doc?.setTextColor(148, 163, 184);
    doc?.setFont('helvetica', 'normal');
    doc?.text(lx?.footer, margin, pageHeight - 9);
    doc?.text(lx?.pageOf(i, pageCount), pageWidth - margin, pageHeight - 9, { align: 'right' });
  }

  return doc;
}

/**
 * Download the Reservation Confirmation PDF
 */
export async function downloadReservationConfirmationPDF(reservation, language = 'en') {
  const doc = await generateReservationConfirmationPDF(reservation, language);
  const refNum = reservation?.referenceNumber || reservation?.reference_number || 'reservation';
  doc?.save(`reservation-confirmation-${refNum}.pdf`);
}

/**
 * Get PDF as base64 string (for storage/email attachment)
 */
export async function getReservationPDFBase64(reservation, language = 'en') {
  const doc = await generateReservationConfirmationPDF(reservation, language);
  return doc?.output('datauristring');
}
